import { useEffect, useState } from 'react'

import { useArchiveFloor } from '../hooks/useArchiveFloor'
import { useEditFloor } from '../hooks/useEditFloor'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { HiOutlineArchive, HiOutlineCheckCircle } from 'react-icons/hi'

import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Center,
  Icon,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Spinner,
  Tooltip,
} from '@chakra-ui/react'

import { Floor } from '@/components/icons'
import { Modal } from '@/components/overlays'
import {
  Button,
  FormFileUploadControl,
  FormInputControl,
  FormInputSelectControl,
  StyledFormInputControl,
} from '@/components/ui'
import { getFacilityOption } from '@/utils/forms/optionBuilders'

import { FloorFormInputsIProps } from '../types/types'
import { ArchiveFloorModal } from './ArchiveFloorModal'
import { EditFloorFormSkeleton } from './EditFloorFormSkeleton'
import { FloorPlanCropper } from './FloorPlanCropper'
import { FloorPlanRemove } from './FloorPlanRemove'
import { PdfPageSelector } from './PdfPageSelector'

interface EditFloorModalIProps {
  floorId: string
  isOpen: boolean
  onClose: () => void
}

export const EditFloorModal = ({
  floorId,
  isOpen,
  onClose,
}: EditFloorModalIProps) => {
  const { t } = useTranslation()
  const mode = useLightMode()
  const isArchiveFloorEnabled = useFeatureFlag(
    FeatureFlag.archiveFloorAndFacility
  )
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false)
  const { unarchiveFloor, isUnarchiveLoading } = useArchiveFloor()

  const handleUnarchive = async () => {
    const isUnarchived = await unarchiveFloor(floorId)
    if (isUnarchived) {
      handleClose()
    }
  }
  const {
    watch,
    handleSubmit,
    register,
    control,
    errors,
    setValue,
    formState,
  } = useForm<FloorFormInputsIProps>({
    reValidateMode: 'onChange',
  })

  const fileUploadWatch = watch('file')
  const {
    floorDetails,
    isFloorDetailsLoading,
    isFloorPlanMarkedForRemoval,
    hasNewFile,
    isNewFileValid,
    canUploadFloorPlan,
    isFloorPlanSwapPending,
    isUpdateLoading,
    selectedPdfPage,
    isPageConfirmed,
    confirmPage,
    unconfirmPage,
    selectedFile,
    isPdf,
    markFloorPlanForRemoval,
    onUpdateFloor,
    setSelectedPdfPage,
    resetPdfPage,
    pdfSelectorRef,
    crop,
    setCrop,
    imagePreviewUrl,
    isPreviewLoading,
    shouldShowCropper,
    cropperRef,
    resetCrop,
    resetFloorPlanChange,
  } = useEditFloor({ floorId, onClose, fileUploadWatch })

  useEffect(() => {
    if (floorDetails) {
      setValue('name', floorDetails?.floor?.name)
      setValue('facility', getFacilityOption(floorDetails?.floor?.facility))
    }
  }, [isFloorDetailsLoading])

  const clearPendingFloorPlanChange = () => {
    setValue('file', null)
    resetFloorPlanChange()
  }

  const handleClose = () => {
    clearPendingFloorPlanChange()
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      motionPreset='slideInBottom'
      onClose={handleClose}
      scrollBehavior={isPdf || shouldShowCropper ? 'inside' : 'outside'}
      size='xl'
    >
      <ModalOverlay />
      <ModalContent bg='#fff'>
        <ModalHeader>
          <Box align-items='center' display='flex' m='20px auto'>
            <Box display='grid' placeItems='center'>
              <Icon as={Floor} boxSize='26px' strokeColor='#000' />
            </Box>
            <Box
              color={`text.primary.${mode}`}
              fontSize='26px'
              fontWeight='extrabold'
              letterSpacing='-0.4px'
              ml={3}
            >
              Edit Floor
            </Box>
            <Spacer />
            <ModalCloseButton color='#000' opacity={0.4} position='initial' />
          </Box>
        </ModalHeader>
        <ModalBody p={0}>
          {isFloorDetailsLoading ? (
            <EditFloorFormSkeleton />
          ) : (
            <Box px={6} py={2}>
              <Box mb={3}>
                <FormInputControl
                  dataTestId='editFloor_modal_floorName'
                  errorMessage={errors.name && errors.name.message}
                  id='name'
                  inputRef={register({
                    required: 'Floor name is required',
                  })}
                  isDisabled={isFloorDetailsLoading}
                  isInvalid={!!errors.name}
                  label='Floor Name'
                  placeholder='Enter floor name'
                />
              </Box>
              <Box mb={3}>
                <FormInputSelectControl
                  control={control}
                  dataTestId='editFloor_modal_facility'
                  defaultValue={getFacilityOption(
                    floorDetails?.floor?.facility
                  )}
                  id='facility'
                  isDisabled={true}
                  label='Facility'
                  placeholder='Add a facility where this floor belongs to'
                />
              </Box>
              <StyledFormInputControl mb={3}>
                {floorDetails?.floor?.floorPlan && (
                  <FloorPlanRemove
                    floorPlanName={floorDetails.floor.floorPlan.name}
                    isMarkedForRemoval={isFloorPlanMarkedForRemoval}
                    isUpdateLoading={isUpdateLoading}
                    onRemove={markFloorPlanForRemoval}
                    onUndo={clearPendingFloorPlanChange}
                  />
                )}
                {canUploadFloorPlan && (
                  <>
                    <Tooltip
                      label='Supported file types include PNG, PDF, JPG, and JPEG'
                      shouldWrapChildren
                    >
                      <FormFileUploadControl
                        accept='.png,.pdf,.jpg,.jpeg'
                        colorScheme={mode}
                        dataTestId='editFloor_modal_floorplan'
                        errorMessage={
                          hasNewFile &&
                          !isNewFileValid &&
                          'File should be in .png, .pdf, .jpg, or .jpeg format'
                        }
                        id='file'
                        inputRef={register({ required: false })}
                        isDisabled={isUpdateLoading}
                        isInvalid={!isNewFileValid}
                        label='Floor Plan'
                        onFileRemoved={() => {
                          setValue('file', null, {
                            shouldDirty: true,
                            shouldValidate: true,
                          })
                          resetPdfPage()
                          resetCrop()
                        }}
                        placeholder='Attach Floor Plan'
                      />
                    </Tooltip>
                    {isFloorPlanSwapPending && (
                      <Alert
                        borderRadius='md'
                        data-testid='EditFloorModal:floorPlanSwapAlert'
                        mt={3}
                        status='info'
                      >
                        <AlertIcon />
                        <AlertDescription fontSize='sm'>
                          {t('floors:crud_swap_save_required')}
                        </AlertDescription>
                      </Alert>
                    )}
                  </>
                )}
              </StyledFormInputControl>
              {isPdf &&
                selectedFile &&
                canUploadFloorPlan &&
                !isPageConfirmed && (
                  <Box ref={pdfSelectorRef}>
                    <PdfPageSelector
                      isDisabled={isUpdateLoading}
                      onConfirm={confirmPage}
                      onPageChange={setSelectedPdfPage}
                      pdfFile={selectedFile}
                      selectedPage={selectedPdfPage}
                    />
                  </Box>
                )}
              {isPreviewLoading && (
                <Center mt={4} py={8}>
                  <Spinner color='blue.500' size='lg' />
                </Center>
              )}
              {shouldShowCropper && imagePreviewUrl && canUploadFloorPlan && (
                <Box ref={cropperRef}>
                  <FloorPlanCropper
                    crop={crop}
                    disabled={isUpdateLoading}
                    imageUrl={imagePreviewUrl}
                    onChangePage={isPdf ? unconfirmPage : undefined}
                    onCropChange={(_, percentCrop) => setCrop(percentCrop)}
                    onReset={resetCrop}
                  />
                </Box>
              )}
            </Box>
          )}
        </ModalBody>
        <ModalFooter pb={6} py={4}>
          <Button mr={3} onClick={handleClose} variant='secondary'>
            Cancel
          </Button>
          {isArchiveFloorEnabled &&
            (floorDetails?.floor?.archived ? (
              <Button
                data-testid='editFloor_modal_unarchiveButton'
                isDisabled={isFloorDetailsLoading || isUpdateLoading}
                isLoading={isUnarchiveLoading}
                leftIcon={<HiOutlineArchive size='24px' />}
                mr={3}
                onClick={handleUnarchive}
                variant='secondary'
              >
                {t('floors:unarchive_button')}
              </Button>
            ) : (
              <Button
                data-testid='editFloor_modal_archiveButton'
                isDisabled={isFloorDetailsLoading || isUpdateLoading}
                leftIcon={<HiOutlineArchive size='24px' />}
                mr={3}
                onClick={() => setIsArchiveModalOpen(true)}
                variant='danger'
              >
                {t('floors:archive_button')}
              </Button>
            ))}
          <Button
            isDisabled={
              isFloorDetailsLoading ||
              (!formState.isDirty && !isFloorPlanMarkedForRemoval) ||
              !isNewFileValid
            }
            isLoading={isUpdateLoading}
            leftIcon={<HiOutlineCheckCircle size='24px' />}
            loadingText='Saving Changes'
            onClick={handleSubmit(onUpdateFloor)}
          >
            Save Changes
          </Button>
        </ModalFooter>
      </ModalContent>
      {isArchiveFloorEnabled && (
        <ArchiveFloorModal
          facilityId={floorDetails?.floor?.facility?.id}
          floorId={floorId}
          floorName={floorDetails?.floor?.name}
          isOpen={isArchiveModalOpen}
          onArchived={handleClose}
          onClose={() => setIsArchiveModalOpen(false)}
        />
      )}
    </Modal>
  )
}
